
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import {
  AppWindow,
  Bell,
  Blocks,
  BookOpen,
  BookText,
  Bot,
  Box,
  Brain,
  Building2,
  ChevronDown,
  CircleCheck,
  ClipboardList,
  DraftingCompass,
  FileText,
  FolderOpen,
  GitBranch,
  GraduationCap,
  LayoutGrid,
  List,
  Lock,
  Menu,
  Puzzle,
  Route,
  Package,
  Settings,
  Shapes,
  ShieldCheck,
  SlidersHorizontal,
  Rocket,
  Rss,
  Server,
  Waypoints,
  Workflow,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useProject } from "@/contexts/ProjectContext";
import {
  Sidebar,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import AssistantChat from "@/components/AssistantChat";
import GuidedDemo from "@/components/GuidedDemo";
import GuidedFlow from "@/components/guided/GuidedFlow";
import { getGuide } from "@/components/guided/guides";
import TutorialFlow from "@/components/tutorial/TutorialFlow";
import { loadProjectGuides, launchGuide } from "@/components/tutorial/guides";
import TutorialChooser from "@/components/tutorial/TutorialChooser";
import { getToursForProject, getTourGroupsForProject } from "@/components/tutorial/tours";
import ChartViewerManager from "@/components/ChartViewer";
import { useIsMobile } from "@/hooks/use-mobile";
import { fetchAlertsBadge, subscribeAlertsChange } from "@/services/alerts";
import { fetchMyRequests } from "@/services/appRequests";
import { useUiView } from "@/hooks/useUiView";
import { listProjects } from "@/services/projects";
import { listAgents } from "@/services/apps";
import { listOrgs, setOrgLogo } from "@/services/orgs";
import { wfEngine } from "@/services/workflows";

// Recipe-authoring pages, collapsed under one expandable Data Dictionary
// group. Order within follows the typical workflow: read the dictionary,
// then drill into the rows that compose it.
// Exported: the share page's governance overlay (SharedApp.jsx) renders the
// same section menu — one list, no drift.
export const dictionaryChildren = [
  { title: "Overview",           page: "data-dictionary",    icon: BookOpen },
  { title: "Elements",           page: "data-elements",      icon: Blocks },
  { title: "Sources",            page: "data-sources",       icon: Box },
  { title: "Source Mappings",    page: "source-mappings",    icon: GitBranch },
  { title: "Reference Lists",    page: "reference-lists",    icon: List },
  { title: "Processing Rules",   page: "processing-rules",   icon: Puzzle },
  { title: "Data Review",        page: "data-review",        icon: DraftingCompass },
  { title: "Quality",            page: "dictionary-quality", icon: CircleCheck },
];

// Data Governance is a TOP-LEVEL nav group (same level as Pipeline + Flow): the
// data dictionary workflow — elements, sources, mappings, reference lists,
// processing rules, review, quality. (The former "Transformations" group and its
// strategies "Overview" item were removed — strategies are an engine concept, not a
// product nav surface.)
const dataGovernanceItems = dictionaryChildren;

// Apps = the project's published views (the *.view Flow endpoints), each a
// full-fledged app surface (e.g. the customers CRUD dashboard). Sits ABOVE
// Data Governance. /apps lists them; /apps/:viewName renders one (embedded
// like Flow studio).
const appsItem = { title: "Apps", page: "apps", icon: AppWindow };
// Agents = the project's first-class agents (separated from apps). Sits
// directly below Apps. /agents lists the caller's visible agents.
const agentsItem = { title: "Agents", page: "agents", icon: Bot };
// Guides = the project's saved in-product walkthroughs (spotlight tours over
// the tutorial engine, stored in the fork vault). Sits directly below Agents;
// /guides lists + launches them and files new_guide/change_guide builds.
const guidesItem = { title: "Guides", page: "guides", icon: GraduationCap };
// Models = first-class CUSTOM ML: models trained on the project's governed
// data, with versions, metrics, the training-data lineage pin and feature
// importances. Deliberately its own surface, not part of Agents — a model is
// deterministic and versioned where an agent is not. Sits between Agents and
// Workflows: models consume governed data, workflows consume models.
const modelsItem = { title: "Models", page: "models", icon: Brain };
// Workflows = first-class business processes (My Work inbox + the visual
// process builder + roles). Sits between Agents and Data Governance.
// Reports = first-class pixel-perfect documents (Typst → PDF) over governed
// data. Between Models and Workflows: models produce numbers, reports present
// them, workflows act on them.
const reportsItem = { title: "Reports", page: "reports", icon: FileText };
const workflowsItem = { title: "Workflows", page: "workflows", icon: Route };
// Audit = first-class per-project compliance: audit runs, findings, open
// issues + fix history (project checks from the auditor fleet, platform
// layer from the Prowler scanner). Sits below Workflows.
const auditItem = { title: "Audit", page: "audit", icon: ShieldCheck };
// Docs = the per-project documentation surface: Specification (the generated
// living spec the project assistant keeps current) + Knowledge Base (concept
// notes, Slack digests, meeting summaries), both over the fork's vault.
const docsItem = { title: "Docs", page: "docs", icon: BookText };
// Portal mode (read-only standalone deployments): trimmed nav — see utils/portal.js.
import { isPortal, isEmbedMode, portalConfig } from "../utils/portal";
import { isMindshineStaff } from "../utils/staff";
const deploymentsItem = { title: "Deployments", page: "deployments", icon: Rocket };
const pipelineItem = { title: "Pipeline", page: "pipeline", icon: Workflow };
// Flow = the embedded Flow studio (full-bleed /flow/*). Separate top-level
// nav item, sits right under Pipeline.
const flowItem = { title: "Flow", page: "flow", icon: Waypoints };
const branchesItem = { title: "Branches", page: "branches", icon: GitBranch };
const projectsItem = { title: "Projects", page: "projects", icon: FolderOpen };
// Organizations = the access-policy manager (orgs, members, roles, project
// ownership). Rendered only for org/platform admins — see SidebarInner.
const organizationsItem = { title: "Organizations", page: "organizations", icon: Building2 };
// App Templates control panel — the library is one platform-wide artifact
// store, so this is a platform_admin surface (the /api/tpl routes are
// server-enforced regardless).
const templatesItem = { title: "Templates", page: "templates", icon: Package };
// Agent fleet = the platform model-routing registry: which model each LLM
// consumer (chat personas, remote fleet workers, backend one-shot tasks)
// runs on. Mindshine-staff surface (same gate as Cortex Audit / Flow).
const fleetItem = { title: "Agent fleet", page: "fleet", icon: Server };
// Cortex Audit = the PLATFORM's own compliance program (repo-level audits,
// control register + evidence, infrastructure layer) — cross-project and
// internal, so it sits in the global section and is staff-gated.
const cortexAuditItem = { title: "Cortex Audit", page: "cortex-audit", icon: ShieldCheck };
const settingsItem = { title: "Settings", page: "settings", icon: Settings };

// The ONLY project pages a share-only user (someone who reaches this project
// through a shared app/agent and nothing else) can actually load. Everything
// else is 403'd by the central project gate. This single list drives BOTH the
// trimmed nav and the route guard below, so the two can never drift apart —
// a nav entry that leads to a blocked page is exactly the dead end we're fixing.
//
// DELIBERATELY MINIMAL (Alex: an app user "should see only list of apps that he
// has access to, ABSOLUTELY NO OTHER CORTEX UI"):
//   apps    — the landing surface and the only thing they came for
//   agents  — only when they actually hold a shared agent (see sharedAgents
//             below); otherwise it is an empty page and gets redirected away
//   profile — the user footer's profile/sign-out target, not a nav item
//   share   — the standalone chrome-less share target, untouched by any of this
// 'projects' and 'settings' USED to be here because they are registry routes
// that technically load; they are gone because "technically loads" is not the
// bar — a person who was handed one app has no business in a project registry
// or a platform settings page.
const SHARED_ONLY_PAGES = ["apps", "profile", "share"];
// 'agents' is conditional, so it is not a constant. Callers pass whether the
// viewer holds any shared agent; unknown (null, still probing) counts as
// allowed, so a direct visit to /agents is never bounced before we know.
const sharedOnlyPages = (hasAgents) =>
  hasAgents === false ? SHARED_ONLY_PAGES : [...SHARED_ONLY_PAGES, "agents"];

// First path segment after /p/<slug>/b/<branch>/ — "" on the project root.
const projectPageOf = (pathname) => {
  const m = String(pathname || "").match(/^\/p\/[^/]+\/b\/[^/]+\/([^/?#]*)/);
  return m ? m[1] : "";
};

// Friendly stand-in for a project page a share-only user cannot open. Without
// it each page renders the gate's raw "No access to project '<slug>'" in its
// own error slot (or an empty shell) — accurate, but it reads as a fault
// rather than "this simply isn't yours to see".
function NotSharedWithYou({ project }) {
  return (
    <div className="min-h-full flex flex-col items-center justify-center gap-3 px-6 py-16 text-center bg-page-bg">
      <Lock size={32} className="text-page-muted" />
      <div className="text-lg font-medium text-page-title">
        This part of {project || "the project"} isn't shared with you
      </div>
      <p className="text-sm text-page-muted max-w-[460px]">
        You have access to specific apps and agents here, not the whole project.
        Open what's been shared with you, or ask the owner for wider access.
      </p>
      <div className="flex items-center gap-2 mt-2">
        <Link
          to={createPageUrl("apps")}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium no-underline hover:bg-nav-active-dark transition-colors"
        >
          <AppWindow size={16} /> Open shared apps
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 h-9 px-4 rounded-[5px] border border-card-border bg-card-bg text-page-text text-sm font-medium no-underline hover:bg-page-bg transition-colors"
        >
          <FolderOpen size={16} /> All projects
        </Link>
      </div>
    </div>
  );
}

function MobileHeader() {
  const { toggleSidebar } = useSidebar();
  return (
    <header className="relative flex items-center justify-center px-6 py-4 md:hidden bg-[var(--nav-from)]">
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label="Toggle sidebar"
        className="absolute left-4 flex items-center justify-center size-8 rounded-lg bg-transparent border-0 cursor-pointer text-white hover:bg-white/10 transition-colors"
      >
        <Menu size={24} />
      </button>
      <h1 className="text-xl font-semibold text-white">TIVA CORTEX</h1>
    </header>
  );
}

function NavItem({ title, url, page, icon: Icon, active, badge, badgeNeutral, tour, compact }) {
  const { setOpenMobile } = useSidebar();
  // Resolve the URL from the page key at render time so the current
  // project/branch (read from window.location by createPageUrl) is applied.
  // An explicit `url` prop (rarely used) still wins.
  const to = url ?? createPageUrl(page);
  return (
    <Link
      to={to}
      onClick={() => setOpenMobile(false)}
      data-active={active || undefined}
      data-tour={tour ?? (page ? `nav-${page}` : undefined)}
      className={`group flex items-center ${compact ? "gap-3 h-8 rounded-[14px] px-2.5 text-[13px]" : "gap-[18px] h-9 rounded-[18px] px-3 text-sm"} shrink-0 no-underline transition-colors
        text-white
        hover:text-nav-hover
        data-[active]:bg-nav-active
        data-[active]:hover:text-white`}
    >
      {Icon && <Icon size={compact ? 18 : 24} className="shrink-0" />}
      <span className="font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">
        {title}
      </span>
      {/* badgeNeutral: informational count (white/20), not an alarm — the
          Board's in-flight builds vs the Alerts bell's red alert count. */}
      {badge > 0 && (
        <span className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full ${badgeNeutral ? "bg-white/20" : "bg-red-500"} text-white text-[10px] font-semibold leading-none`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

function NavSeparator() {
  return <div className="h-px shrink-0 bg-nav-separator" />;
}

// Expandable parent in the sidebar. Auto-opens whenever a child is active,
// so navigating to a child page surfaces the group without manual clicks.
// Persists collapsed state in localStorage so a user's preference survives
// reloads, but the auto-open-when-active rule always wins.
function NavGroup({ title, icon: Icon, items, isActive }) {
  const anyActive = items.some(it => isActive(it.url ?? createPageUrl(it.page)));
  const storageKey = `nav-group:${title}`;
  const [userOpen, setUserOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? false : v === "1";
    } catch {
      return false;
    }
  });
  const open = userOpen || anyActive;

  const toggle = () => {
    const next = !userOpen;
    setUserOpen(next);
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch {}
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={toggle}
        data-active={anyActive || undefined}
        data-tour={`nav-group-${title.toLowerCase().replace(/\s+/g, "-")}`}
        className="group flex items-center gap-[18px] h-9 rounded-[18px] px-3 shrink-0 border-0 bg-transparent cursor-pointer transition-colors
          text-white text-left
          hover:text-nav-hover
          data-[active]:text-nav-hover"
      >
        {Icon && <Icon size={24} className="shrink-0" />}
        <span className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">
          {title}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open && (
        <div className="flex flex-col gap-1 pl-6">
          {items.map(it => (
            it.children
              ? <NavSubGroup key={it.title} {...it} isActive={isActive} />
              : <NavItem key={it.title} {...it} active={isActive(it.url ?? createPageUrl(it.page))} compact />
          ))}
        </div>
      )}
    </div>
  );
}

// A nav item that is itself expandable into a child workflow (e.g. the
// Processing Rules strategy nests the data-dictionary pages as its config).
function NavSubGroup({ title, url, page, icon, children, isActive }) {
  const to = url ?? createPageUrl(page);
  const anyActive = isActive(to) || children.some(c => isActive(c.url ?? createPageUrl(c.page)));
  const [open, setOpen] = useState(false);
  const expanded = open || anyActive;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center">
        <NavItem title={title} url={to} icon={icon} active={isActive(to)} compact />
        <button type="button" onClick={() => setOpen(v => !v)} aria-label={`Toggle ${title}`}
          className="ml-1 shrink-0 border-0 bg-transparent cursor-pointer text-white/70 hover:text-white">
          <ChevronDown size={14} className={`transition-transform ${expanded ? "" : "-rotate-90"}`} />
        </button>
      </div>
      {expanded && (
        <div className="flex flex-col gap-1 pl-5">
          {children.map(c => (
            <NavItem key={c.title} {...c} active={isActive(c.url ?? createPageUrl(c.page))} compact />
          ))}
        </div>
      )}
    </div>
  );
}

const USER_MENU_ITEM_CLASS =
  "flex items-center gap-2 h-[30px] px-4 text-sm font-medium text-nav-active-hover cursor-pointer outline-none select-none transition-colors data-[highlighted]:text-nav-active first:mt-[10px] last:mb-[10px]";

function ProfileIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path
        d="M20 21C20 19.6044 20 18.9067 19.8278 18.3389C19.44 17.0605 18.4395 16.06 17.1611 15.6722C16.5933 15.5 15.8956 15.5 14.5 15.5H9.5C8.10444 15.5 7.40665 15.5 6.83886 15.6722C5.56045 16.06 4.56004 17.0605 4.17224 18.3389C4 18.9067 4 19.6044 4 21M16.5 7.5C16.5 9.98528 14.4853 12 12 12C9.51472 12 7.5 9.98528 7.5 7.5C7.5 5.01472 9.51472 3 12 3C14.4853 3 16.5 5.01472 16.5 7.5Z"
        stroke="var(--nav-active)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path
        d="M16 17L21 12M21 12L16 7M21 12H9M9 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21H9"
        stroke="var(--nav-active)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function ThemeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
        stroke="var(--nav-active)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
      <path
        d="M16 8.5C16 12.6421 12.6421 16 8.5 16C7.88534 16 7.28795 15.9261 6.71623 15.7866C7.89585 17.4297 9.82294 18.5 12 18.5C15.5899 18.5 18.5 15.5899 18.5 12C18.5 9.82294 17.4297 7.89585 15.7866 6.71623C15.9261 7.28795 16 7.88534 16 8.5Z"
        stroke="var(--nav-active)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function UserMenu({ initials, fullName, onProfile, onLogout, theme, onToggleTheme }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  // Simple ⇄ Advanced view lives here, first item above the theme toggle —
  // same "shows the view you switch TO" phrasing as the theme item. Portal
  // deployments keep their own fixed nav, so the item is meaningless there.
  const { simple, setUiView } = useUiView();
  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <div
        className="shrink-0 flex w-full cursor-pointer text-white hover:text-nav-hover transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <DropdownMenuPrimitive.Trigger asChild>
          {/* testid: the share-only nav trims everything ELSE, and this footer
              is the only remaining way to reach Profile / sign out — worth an
              explicit hook so the trim can never quietly take it too. */}
          <button
            type="button"
            data-testid="user-menu-trigger"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 h-9 px-3 max-w-full bg-transparent border-0 outline-none cursor-pointer"
          >
            <div className="size-6 rounded-full bg-nav-avatar-bg flex items-center justify-center shrink-0 text-white text-xs font-medium">
              {initials}
            </div>
            <span className="min-w-0 text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap text-left">
              {fullName}
            </span>
          </button>
        </DropdownMenuPrimitive.Trigger>
      </div>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          side={isMobile ? "top" : "right"}
          align={isMobile ? "start" : "end"}
          sideOffset={8}
          collisionPadding={8}
          className="z-50 min-w-[200px] rounded-[5px] bg-card-bg shadow-[0_8px_40px_0_var(--shadow-blue-md)] overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
        >
          {!isPortal() && (
            <DropdownMenuPrimitive.Item
              onSelect={() => setUiView(simple ? "advanced" : "simple")}
              data-testid="ui-view-toggle"
              className={USER_MENU_ITEM_CLASS}
            >
              <SlidersHorizontal size={24} className="shrink-0" style={{ color: "var(--nav-active)" }} />
              {simple ? "Advanced mode" : "Standard mode"}
            </DropdownMenuPrimitive.Item>
          )}
          <DropdownMenuPrimitive.Item onSelect={onToggleTheme} className={USER_MENU_ITEM_CLASS}>
            <ThemeIcon />
            {theme === 'dark' ? 'Light theme' : 'Dark theme'}
          </DropdownMenuPrimitive.Item>
          <DropdownMenuPrimitive.Item onSelect={onProfile} className={USER_MENU_ITEM_CLASS}>
            <ProfileIcon />
            User Profile
          </DropdownMenuPrimitive.Item>
          <DropdownMenuPrimitive.Item onSelect={onLogout} className={USER_MENU_ITEM_CLASS}>
            <LogoutIcon />
            Log Out
          </DropdownMenuPrimitive.Item>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

function ActiveProjectBranchChip({ project, branch }) {
  // Portal deployments are pinned to ONE project + branch — the switcher
  // chips are meaningless there (and link to hidden cortex pages).
  if (isPortal()) return null;
  if (!project) {
    // No project selected → CTA chip that takes user to /projects to create one.
    return (
      <Link
        to={createPageUrl("projects")}
        title="Create your first project"
        data-tour="project-branch-chip"
        className="flex items-center gap-2 h-7 px-2 rounded-[10px] bg-white/10 hover:bg-white/15 text-white/80 text-xs no-underline transition-colors"
      >
        <FolderOpen size={14} className="shrink-0" />
        <span className="font-medium">Create project</span>
      </Link>
    );
  }
  return (
    <div className="flex items-center gap-1" data-tour="project-branch-chip">
      <Link
        to={createPageUrl("projects")}
        title="Manage projects"
        className="flex items-center gap-2 h-7 px-2 rounded-[10px] bg-white/10 hover:bg-white/15 text-white text-xs no-underline transition-colors"
      >
        <FolderOpen size={14} className="shrink-0" />
        <span className="font-medium truncate max-w-[120px]">{project}</span>
      </Link>
      <span className="text-white/50 text-xs">/</span>
      <Link
        to={createPageUrl("branches")}
        title="Manage branches"
        className="flex items-center gap-2 h-7 px-2 rounded-[10px] bg-white/10 hover:bg-white/15 text-white text-xs no-underline transition-colors"
      >
        <GitBranch size={14} className="shrink-0" />
        <span className="font-medium truncate max-w-[120px]">{branch || "main"}</span>
      </Link>
    </div>
  );
}

// Simple-view project chip: an in-place switcher popover (Slack/Notion
// pattern) instead of the trip through /projects, and NO branch chip — the
// branch is a git concept the Simple view keeps out of the chrome (switching
// stays on the current page via switchProject; the branch resets to main).
function ProjectSwitcherChip({ project }) {
  const { switchProject } = useAuth();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState(null); // lazy: fetched on first open
  useEffect(() => {
    if (!open || projects !== null) return;
    listProjects().then(setProjects).catch(() => setProjects([]));
  }, [open, projects]);
  const go = (p) => {
    if (p.slug === project) return;
    // Share-only projects 403 every page except the shared surfaces — land on
    // Apps there instead of wherever the caller happened to be.
    if (p.shared_surface_only) window.location.assign(`/p/${encodeURIComponent(p.slug)}/b/main/apps`);
    else switchProject(p.slug);
  };
  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          title="Switch project"
          data-tour="project-branch-chip"
          className="flex items-center gap-2 h-7 px-2 rounded-[10px] bg-white/10 hover:bg-white/15 text-white text-xs border-0 cursor-pointer transition-colors"
        >
          <FolderOpen size={14} className="shrink-0" />
          <span className="font-medium truncate max-w-[140px]">{project}</span>
          <ChevronDown size={12} className="shrink-0 opacity-70" />
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 min-w-[220px] max-w-[300px] max-h-[60vh] overflow-y-auto rounded-[5px] bg-card-bg shadow-[0_8px_40px_0_var(--shadow-blue-md)] py-1"
        >
          {projects === null ? (
            <div className="px-3 py-2 text-xs text-page-muted">Loading…</div>
          ) : !projects.length ? (
            <div className="px-3 py-2 text-xs text-page-muted">No projects</div>
          ) : (
            projects.map((p) => (
              <DropdownMenuPrimitive.Item key={p.slug} onSelect={() => go(p)} className={USER_MENU_ITEM_CLASS}>
                <span className="truncate">{p.name || p.slug}</span>
                {p.slug === project && <span className="ml-auto text-nav-active shrink-0">✓</span>}
              </DropdownMenuPrimitive.Item>
            ))
          )}
          <div className="border-t border-card-border mt-1 pt-1">
            <DropdownMenuPrimitive.Item asChild>
              <Link to={createPageUrl("projects")} className={`${USER_MENU_ITEM_CLASS} no-underline`}>
                All projects →
              </Link>
            </DropdownMenuPrimitive.Item>
          </div>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

// Sidebar logo: the Cortex mark by default, or the ACTIVE org's custom logo
// when one is set (org branding). Platform admins get hover controls — Edit
// (upload) and, when a custom logo is set, Reset — mirroring the
// platform_admin-only PUT /api/orgs/:slug/logo.
function OrgLogo({ activeProject }) {
  const { user } = useAuth();
  const isPlatformAdmin = !!user?.orgs?.some((o) => o.role === "platform_admin");
  const [org, setOrg] = useState(null); // { slug, logo_data } of the active org
  const fileRef = useRef(null);
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const [projects, orgs] = await Promise.all([listProjects(), listOrgs()]);
        const slug = projects.find((p) => p.slug === activeProject)?.org_slug || "mindshine";
        const row = orgs.find((o) => o.slug === slug);
        if (!gone) setOrg({ slug, logo_data: row?.logo_data || null });
      } catch {
        if (!gone) setOrg(null);
      }
    })();
    return () => { gone = true; };
  }, [activeProject]);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !org) return;
    if (file.size > 300 * 1024) { alert("Logo image must be under 300KB."); return; }
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    try {
      await setOrgLogo(org.slug, dataUrl);
      setOrg((o) => ({ ...o, logo_data: dataUrl }));
    } catch (err) {
      alert(`Could not save logo: ${err?.message || err}`);
    }
  };
  const onReset = async () => {
    if (!org) return;
    try {
      await setOrgLogo(org.slug, null);
      setOrg((o) => ({ ...o, logo_data: null }));
    } catch (err) {
      alert(`Could not reset logo: ${err?.message || err}`);
    }
  };
  return (
    <div className="relative shrink-0 w-[183px] h-[60px] group" data-testid="org-logo">
      <Link to="/" className="block w-full h-full">
        <img
          src={org?.logo_data || "/logo.svg"}
          alt="Logo"
          className="w-full h-full object-contain"
        />
      </Link>
      {isPlatformAdmin && org && (
        <div className="absolute top-0.5 right-0.5 hidden group-hover:flex gap-1">
          <button
            type="button"
            data-testid="org-logo-edit"
            title={`Change the ${org.slug} organization logo`}
            onClick={() => fileRef.current?.click()}
            className="w-6 h-6 flex items-center justify-center rounded bg-white/90 border border-gray-300 text-gray-700 text-xs shadow hover:bg-white"
          >✎</button>
          {org.logo_data && (
            <button
              type="button"
              data-testid="org-logo-reset"
              title="Restore the default Cortex logo"
              onClick={onReset}
              className="w-6 h-6 flex items-center justify-center rounded bg-white/90 border border-gray-300 text-gray-700 text-xs shadow hover:bg-white"
            >↺</button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
            className="hidden"
            onChange={onPick}
          />
        </div>
      )}
    </div>
  );
}

// `sharedAgents` — does this share-only viewer hold at least one shared agent?
// true / false / null (still probing). Lifted to Layout so the ONE probe drives
// both the nav item and the route guard; see the sharedAgents effect there.
function SidebarInner({ isActive, initials, fullName, onProfile, onLogout, theme, onToggleTheme, alertsCount, boardCount, activeBranch, activeProject, sharedAgents }) {
  // Organizations (the access-policy manager) is an admin surface: shown only
  // to users who administer at least one org. Enforcement is server-side —
  // hiding the item is purely presentational.
  const { user } = useAuth();
  const { sharedSurfaceOnly } = useProject();
  // Simple vs Advanced view — presentational only (see hooks/useUiView.js);
  // the switch itself lives in the profile menu (UserMenu). Portal and
  // share-only modes keep their own trimmed navs; Simple applies only to the
  // full nav below.
  const { simple } = useUiView();
  // Workflows earns its Simple nav slot only when the project actually has
  // processes — a permanently empty "Nothing waiting on you" page is exactly
  // the noise Simple mode removes. Probed via the same allowlisted engine
  // call the Workflows page opens with; any failure (no postgres target yet,
  // engine down) just hides the item.
  const [wfAvailable, setWfAvailable] = useState(false);
  useEffect(() => {
    if (!simple || !activeProject || isPortal() || sharedSurfaceOnly) { setWfAvailable(false); return; }
    let gone = false;
    wfEngine("list_definitions", { status: "published" })
      .then((r) => { if (!gone) setWfAvailable((r.definitions || []).length > 0); })
      .catch(() => { if (!gone) setWfAvailable(false); });
    return () => { gone = true; };
  }, [simple, activeProject, sharedSurfaceOnly]);
  const showOrgs = !!user?.orgs?.some((o) => o.role === "platform_admin" || o.role === "org_admin");
  // The template library is one platform-wide artifact store, so Templates
  // (its control panel) is a PLATFORM surface: platform_admin, from anywhere
  // — not scoped to the org or project that happens to hold the library
  // (docs/app-templates.md §4.1). /api/tpl stays server-enforced regardless.
  const isPlatformAdmin = !!user?.orgs?.some((o) => o.role === "platform_admin");
  const showTemplates = isPlatformAdmin;
  // Feed (the org-wide Slack/meetings knowledge surface): any real org member
  // — each org brings its own Slack workspace, users bring personal tokens;
  // the backend scopes every read to the caller's orgs and keeps org-level
  // controls to org admins (backend/modules/feed).
  const showFeed = !!user?.orgs?.some((o) => ["platform_admin", "org_admin", "member"].includes(o.role));
  // Flow (the embedded Flow studio) is an INTERNAL engineering surface: it edits
  // the endpoints and services every project runs on, so it is restricted to
  // @mindshine.com staff by email domain. Gating on the email rather than on org
  // membership is deliberate — an external collaborator can be added to the
  // mindshine org for project access without thereby gaining the engine.
  // Nav-only: /flow/* stays routable, so this hides the door, it does not lock it.
  const showFlow = isMindshineStaff(user);
  // Agent fleet (the model-routing registry) follows the Cortex Audit model:
  // an INTERNAL operational surface gated by mindshine staff domain only (any
  // role); /api/fleet accepts any authenticated user, like /api/audit/engine.
  const showFleet = isMindshineStaff(user);
  return (
    <>
      {/* Logo — the active org's custom mark when set (platform-admin editable on hover) */}
      <OrgLogo activeProject={activeProject} />

      {/* Current project / branch chip. Simple view: an in-place project
          switcher, no branch chip (git stays out of the everyday chrome). */}
      {simple && !isPortal() && activeProject ? (
        <div className="flex items-center">
          <ProjectSwitcherChip project={activeProject} />
        </div>
      ) : (
        <ActiveProjectBranchChip project={activeProject} branch={activeBranch} />
      )}

      {/* Nav — organized into four semantic sections:
            1. Project overview (Dashboard, Alerts)
            2. Recipe authoring (collapsible Data Dictionary)
            3. Runtime + versioning (Pipeline, Branches)
            4. Admin (Projects, Settings) */}
      <nav className="flex-1 flex flex-col gap-2 overflow-y-auto min-h-0">
        {isPortal() ? (
          // PORTAL (read-only standalone deployment): Apps, Agents, Workflows,
          // Data Governance, Pipeline — plus "Flow" opening the exported
          // endpoints dashboard on its own domain.
          <>
            <NavItem {...appsItem} active={isActive(createPageUrl(appsItem.page))} />
            <NavItem {...agentsItem} active={isActive(createPageUrl(agentsItem.page))} />
            <NavItem {...workflowsItem} active={isActive(createPageUrl(workflowsItem.page))} />
            <NavGroup
              title="Data Governance"
              icon={Puzzle}
              items={dataGovernanceItems}
              isActive={isActive}
            />
            <NavItem {...pipelineItem} active={isActive(createPageUrl(pipelineItem.page))} />
            {portalConfig()?.flowUrl && (
              <a
                href={portalConfig().flowUrl}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-[18px] h-9 rounded-[18px] px-3 shrink-0 no-underline transition-colors text-white hover:text-nav-hover"
              >
                <Workflow size={24} className="shrink-0" />
                <span className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">Flow</span>
              </a>
            )}
          </>
        ) : sharedSurfaceOnly ? (
          // SHARE-ONLY: the caller reaches this project through a shared app or
          // agent and nothing else — the central PEP 403s every other
          // project-scoped route (only the shared-surface reads are let
          // through). The nav is therefore APPS ONLY: the one list of things
          // they were given, and nothing that would be a dead end.
          //
          // Agents earns a slot only when the viewer actually holds a shared
          // agent (/api/agents is per-viewer filtered server-side — an
          // app_user with no agent share gets []). An always-present Agents
          // item was a permanently empty page for most of them.
          //
          // Projects and Settings are deliberately absent: they load (registry
          // routes, not project-scoped) but they are cortex's own furniture,
          // and this viewer is not here for cortex. Profile and sign-out stay
          // reachable through the user footer below, which is why /profile
          // remains in SHARED_ONLY_PAGES without being a nav item.
          //
          // Presentational only; the server is the enforcement point. These
          // entries MUST stay within sharedOnlyPages() — anything else here
          // would be a link the route guard immediately redirects away from.
          <>
            <NavItem {...appsItem} active={isActive(createPageUrl(appsItem.page))} />
            {sharedAgents && (
              <NavItem {...agentsItem} active={isActive(createPageUrl(agentsItem.page))} />
            )}
          </>
        ) : simple ? (
          // SIMPLE VIEW: the everyday surfaces only — Apps (also the landing
          // page, see ProjectHome in main.jsx), Agents, Board, Docs, plus
          // Workflows when the project actually has processes. No Projects
          // item: the chip's switcher popover covers switching, and its
          // "All projects →" link (or the no-project "Create project" CTA)
          // reaches the page — the Simple path to creating/changing a
          // project, since the trimmed New-task picker drops those kinds.
          // Everything else stays reachable by direct URL (links from
          // chat/alerts must keep working) — presentation, not access control.
          <>
            <NavItem {...appsItem} active={isActive(createPageUrl(appsItem.page))} />
            <NavItem {...agentsItem} active={isActive(createPageUrl(agentsItem.page))} />
            <NavItem {...guidesItem} active={isActive(createPageUrl(guidesItem.page))} />
            <NavItem title="Board" page="board" icon={ClipboardList} active={isActive(createPageUrl("board"))} badge={boardCount} badgeNeutral tour="my-requests" />
            {wfAvailable && (
              <NavItem {...workflowsItem} active={isActive(createPageUrl(workflowsItem.page))} />
            )}
            <NavItem {...docsItem} active={isActive(createPageUrl(docsItem.page))} />
            {/* Feed — same audience gate as Advanced (showFeed); most
                Standard users never see this item. */}
            {showFeed && (
              <NavItem title="Feed" page="feed" icon={Rss} active={isActive(createPageUrl("feed"))} />
            )}
          </>
        ) : (
          <>
        <NavItem title="Dashboard" page="dashboard" icon={LayoutGrid} active={isActive(createPageUrl("dashboard"))} />
        <NavItem title="Alerts" page="alerts" icon={Bell} active={isActive(createPageUrl("alerts"))} badge={alertsCount} />
        {/* Board — the kanban page over the caller's own app/agent requests,
            cross-project. The neutral badge counts in-flight builds
            (queued/claimed/running). tour="my-requests" keeps the e2e/tutorial
            anchor that predates the popover → page move. */}
        <NavItem title="Board" page="board" icon={ClipboardList} active={isActive(createPageUrl("board"))} badge={boardCount} badgeNeutral tour="my-requests" />
        {/* Feed — the cortex-wide Slack knowledge surface (digest topics +
            the opt-in channel registry). Cross-project like Board;
            platform_admin-only, like the backend routes it reads. */}
        {showFeed && (
          <NavItem title="Feed" page="feed" icon={Rss} active={isActive(createPageUrl("feed"))} />
        )}
        <NavSeparator />
        <NavItem {...appsItem} active={isActive(createPageUrl(appsItem.page))} />
        <NavItem {...agentsItem} active={isActive(createPageUrl(agentsItem.page))} />
        <NavItem {...guidesItem} active={isActive(createPageUrl(guidesItem.page))} />
        <NavItem {...modelsItem} active={isActive(createPageUrl(modelsItem.page))} />
        <NavItem {...reportsItem} active={isActive(createPageUrl(reportsItem.page))} />
        <NavItem {...workflowsItem} active={isActive(createPageUrl(workflowsItem.page))} />
        <NavItem {...auditItem} active={isActive(createPageUrl(auditItem.page))} />
        <NavItem {...docsItem} active={isActive(createPageUrl(docsItem.page))} />
        <NavItem {...deploymentsItem} active={isActive(createPageUrl(deploymentsItem.page))} />
        <NavGroup
          title="Data Governance"
          icon={Puzzle}
          items={dataGovernanceItems}
          isActive={isActive}
        />
        <NavItem {...pipelineItem} active={isActive(createPageUrl(pipelineItem.page))} />
        {showFlow && (
          <NavItem {...flowItem} active={isActive(createPageUrl(flowItem.page))} />
        )}
        <NavSeparator />
        <NavItem {...branchesItem} active={isActive(createPageUrl(branchesItem.page))} />
        <NavItem {...projectsItem} active={isActive(createPageUrl(projectsItem.page))} />
        {showOrgs && (
          <NavItem {...organizationsItem} active={isActive(createPageUrl(organizationsItem.page))} />
        )}
        {showFleet && (
          <NavItem {...fleetItem} active={isActive(createPageUrl(fleetItem.page))} />
        )}
        {showTemplates && (
          <NavItem {...templatesItem} active={isActive(createPageUrl(templatesItem.page))} />
        )}
        {showFlow && (
          <NavItem {...cortexAuditItem} active={isActive(createPageUrl(cortexAuditItem.page))} />
        )}
        <NavSeparator />
        <NavItem {...settingsItem} active={isActive(createPageUrl(settingsItem.page))} />
        {/* Tutorial launcher — starts the in-product spotlight tour (chooser
            when the active project has its own tours besides the core one). */}
        <button
          type="button"
          data-tour="nav-tutorial"
          onClick={() => window.dispatchEvent(new CustomEvent("cortex:start-tutorial"))}
          className="group flex items-center gap-[18px] h-9 rounded-[18px] px-3 shrink-0 border-0 bg-transparent cursor-pointer transition-colors text-white text-left hover:text-nav-hover"
        >
          <GraduationCap size={24} className="shrink-0" />
          <span className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">
            Tutorial
          </span>
        </button>
          </>
        )}
      </nav>

      {/* Footer — user menu */}
      <UserMenu initials={initials} fullName={fullName} onProfile={onProfile} onLogout={onLogout} theme={theme} onToggleTheme={onToggleTheme} />
    </>
  );
}

// Modal host for the app speed dial (AssistantChat): an app is the user's
// whole screen, and mid-flow they still need cortex's own surfaces — the
// Pipeline canvas, Data Governance. Those open HERE, as chrome-less (?embed=1)
// pages in a modal over the app, instead of navigating away.
function EmbedPageModal({ projectSlug, branch }) {
  const [open, setOpen] = useState(null); // { page, label, group? }
  const [cur, setCur] = useState(null);   // active page within the modal
  useEffect(() => {
    const on = (e) => { if (e.detail && e.detail.page) { setOpen(e.detail); setCur(e.detail.page); } };
    window.addEventListener("cortex:open-embed-page", on);
    return () => window.removeEventListener("cortex:open-embed-page", on);
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    if (open) { window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }
  }, [open]);
  if (!open || !projectSlug) return null;
  // The governance modal carries the SAME section menu as the Data Governance
  // nav group — without it there is no way to move between Elements, Sources,
  // Rules etc. from inside an app.
  const menu = open.group === "governance" ? dictionaryChildren : null;
  const src = `/p/${projectSlug}/b/${branch || "main"}/${cur || open.page}?embed=1`;
  return (
    <div className="fixed inset-0 z-[100000] bg-black/50 flex items-center justify-center p-6" data-testid="embed-page-modal"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full h-full max-w-[1500px] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 h-12 border-b shrink-0">
          <div className="font-semibold text-sm">{open.label || open.page} — {projectSlug}</div>
          <button className="p-1 rounded hover:bg-black/5" data-testid="embed-modal-close" onClick={() => setOpen(null)} title="Close">✕</button>
        </div>
        <div className="flex-1 flex min-h-0">
          {menu ? (
            <nav className="w-52 shrink-0 border-r overflow-y-auto py-2 bg-slate-50 dark:bg-slate-800/40" data-testid="embed-modal-menu">
              {menu.map((it) => (
                <button key={it.page} onClick={() => setCur(it.page)}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 border-0 cursor-pointer transition-colors ${
                    (cur || open.page) === it.page
                      ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-medium"
                      : "bg-transparent hover:bg-black/5 text-slate-700 dark:text-slate-300"}`}>
                  {it.icon ? <it.icon size={15} className="shrink-0" /> : null}
                  {it.title}
                </button>
              ))}
            </nav>
          ) : null}
          <iframe key={src} title={open.label || open.page} src={src} data-testid="embed-page-iframe" className="flex-1 w-full border-0" />
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { projectSlug, branch, sharedSurfaceOnly, projectsLoading } = useProject();

  // Does this share-only viewer hold any shared AGENT? /api/agents is
  // per-viewer filtered server-side (an app_user with no agent share gets []),
  // so this reads the existing API rather than adding client-side authz.
  // null = not probed yet; the nav item and the route guard both wait on it, so
  // /agents is never bounced before the answer is in. Only fired for a
  // share-only viewer — a member's chrome must not depend on this call.
  const [sharedAgents, setSharedAgents] = useState(null);
  useEffect(() => {
    if (!sharedSurfaceOnly || !projectSlug) { setSharedAgents(null); return; }
    let gone = false;
    listAgents()
      .then((r) => { if (!gone) setSharedAgents(((r?.agents) || []).length > 0); })
      .catch(() => { if (!gone) setSharedAgents(false); });
    return () => { gone = true; };
  }, [sharedSurfaceOnly, projectSlug]);

  const page = projectPageOf(location.pathname);
  const pageIsAllowed = sharedOnlyPages(sharedAgents).includes(page);

  // Share-only caller on a project page that is not theirs.
  const blockedForShareOnly = sharedSurfaceOnly && !pageIsAllowed;

  // The AMBIGUOUS window, and why it needs its own state. sharedSurfaceOnly is
  // false until GET /api/projects lands, so on the first render we do not yet
  // know which kind of user this is. Rendering the Outlet optimistically means a
  // share-only user's blocked page MOUNTS — and its fetch effects fire — before
  // the flag flips and the redirect below runs. That is precisely the 403 spray:
  // ~9 refused requests per blocked route, from a page that was never theirs.
  //
  // So: while the answer is unknown AND the route is one a share-only user
  // could not have, hold the Outlet for that one beat. Scoped deliberately —
  // an ALLOWED route (apps, profile) renders immediately for everyone, and a
  // member is held only on the first paint of a project page, by the same fetch
  // the chrome around them is already waiting on.
  const undecided = projectsLoading && !pageIsAllowed;

  // …and a blocked route is a REDIRECT, not a page. An app user who signs in
  // lands on whatever route they last visited (ProtectedRoute stashes it,
  // Login replays it) — for them that was typically a project page they cannot
  // open, so the first thing they saw was a wall. Sending them to their apps
  // list instead is the whole point: the list of apps IS their product.
  //
  // Redirecting rather than rendering also stops the 403 spray: the blocked
  // page's own data fetches never mount, so nothing fires against routes the
  // PEP will refuse. (Deliverable d — this is where it is satisfied; the
  // Outlet for a blocked route is simply never rendered.)
  //
  // NotSharedWithYou survives for the case it is actually right for: a deep
  // link INTO an allowed page family that is not shared — /apps/<a-view-they-
  // cannot-open> — where the page itself, not the route, is the refusal.
  const appsUrl = projectSlug
    ? `/p/${encodeURIComponent(projectSlug)}/b/${encodeURIComponent(branch || "main")}/apps`
    : null;
  const alreadyOnApps = appsUrl && location.pathname === appsUrl;
  useEffect(() => {
    if (blockedForShareOnly && appsUrl && !alreadyOnApps) {
      navigate(appsUrl, { replace: true });
    }
  }, [blockedForShareOnly, appsUrl, alreadyOnApps, navigate]);
  const [charts, setCharts] = useState([]);
  const chartIndexRef = useRef(0);
  const [alertsCount, setAlertsCount] = useState(0);
  const [boardCount, setBoardCount] = useState(0);

  // Guided overlay: started from the Projects "New Project → Guided" chooser
  // (cortex:start-guided-demo event). The user names the project inside the
  // panel — no slug is predetermined. Mounted here in Layout so it survives
  // navigation (e.g. when a step routes to /pipeline). No page reload happens
  // during the walkthrough, so nothing needs to persist/resume.
  const [guidedActive, setGuidedActive] = useState(false);
  useEffect(() => {
    const onStart = () => setGuidedActive(true);
    window.addEventListener("cortex:start-guided-demo", onStart);
    return () => window.removeEventListener("cortex:start-guided-demo", onStart);
  }, []);

  // Per-entity Guided walkthroughs (Add Source / Rule / Target …). The chooser on
  // each New/Add button dispatches `cortex:start-guided` with { guide: <key> };
  // mounted here in Layout so the overlay survives the route changes its steps make.
  const [guidedEntity, setGuidedEntity] = useState(null); // { key, kind } or null
  useEffect(() => {
    const onStart = (e) => { const k = e.detail?.guide; if (k) setGuidedEntity({ key: k, kind: e.detail?.kind || null }); };
    window.addEventListener("cortex:start-guided", onStart);
    return () => window.removeEventListener("cortex:start-guided", onStart);
  }, []);

  // Spotlight tutorial (explanatory tour; see components/tutorial/). Started
  // by the sidebar Tutorial launcher or a `cortex:start-tutorial` event, with
  // an optional { tour: <key> } to skip the chooser. Mounted here so it
  // survives the route changes its steps perform.
  const [tutorial, setTutorial] = useState(null);          // active tour object
  const [tutorialChooser, setTutorialChooser] = useState(false);
  useEffect(() => {
    const onStart = (e) => {
      const tours = getToursForProject(projectSlug);
      const key = e.detail?.tour;
      if (key) {
        const t = tours.find((x) => x.key === key);
        if (t) { setTutorial(t); return; }
      }
      if (tours.length === 1) setTutorial(tours[0]);
      else setTutorialChooser(true);
    };
    window.addEventListener("cortex:start-tutorial", onStart);
    return () => window.removeEventListener("cortex:start-tutorial", onStart);
  }, [projectSlug]);

  // SAVED GUIDES — agent-generated tours stored in the project's fork vault
  // (type 'guide'). Loaded + registered on project switch so the Tutorial
  // chooser and the chat rail can launch them; a `cortex:launch-guide`
  // message from the chat iframe launches one by its vault slug.
  useEffect(() => {
    if (!projectSlug) return;
    // Skipped for a share-only viewer: guides live in the fork vault behind
    // /api/docs/engine, which the PEP 403s for them. There is no Tutorial
    // launcher and no chat rail in their chrome to launch a guide from, so the
    // call could only ever produce a console 403. Waiting out projectsLoading
    // matters as much as the flag: sharedSurfaceOnly is false until the project
    // list lands, so firing early puts the refusal in the console regardless.
    if (projectsLoading || sharedSurfaceOnly) return;
    loadProjectGuides(projectSlug).catch(() => {});
  }, [projectSlug, sharedSurfaceOnly, projectsLoading]);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type !== "cortex:launch-guide" || !e.data.slug) return;
      launchGuide(projectSlug, String(e.data.slug)).then((ok) => {
        // Not a launchable tour (e.g. a markdown runbook saved under guide-*):
        // open it on the Docs page instead of silently doing nothing.
        if (!ok) navigate(createPageUrl(`docs?tab=kb&doc=${encodeURIComponent(String(e.data.slug))}`));
      }).catch(() => {});
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [projectSlug, navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "show_chart") {
        const id = `chart-${Date.now()}-${chartIndexRef.current++}`;
        setCharts(prev => [...prev, { id, config: e.data.config }]);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const refresh = () => {
      fetchAlertsBadge()
        .then(res => { if (!cancelled) setAlertsCount(res?.count || 0); })
        .catch(() => {});
      // Board badge: the TEAM's in-flight builds (queued/claimed/running),
      // cross-project — same story the (team-scoped by default) Board tells.
      // Same cadence as the alerts bell — build completions fire the same
      // alerts pub/sub, so both badges move together.
      fetchMyRequests({ status: "open", scope: "team" })
        .then(res => { if (!cancelled) setBoardCount(res?.open_count ?? (res?.requests || []).length); })
        .catch(() => {});
    };
    refresh();
    const unsubscribe = subscribeAlertsChange(refresh);
    const intervalId = setInterval(refresh, 30000);
    return () => {
      cancelled = true;
      unsubscribe();
      clearInterval(intervalId);
    };
  }, [location.pathname, user]);

  const initials = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map(n => n[0].toUpperCase())
    .join("") || "U";

  const fullName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map(n => n.charAt(0).toUpperCase() + n.slice(1))
    .join(" ") || "User";

  const isActive = (url) =>
    // Flow is a full-bleed sub-router (/flow/*) and Apps has a /apps/:viewName
    // sub-route; keep those items highlighted on any sub-route. Others exact-match.
    // URLs are now project-prefixed (/p/<slug>/b/<branch>/…), so match the
    // resolved item url as a path prefix for these two.
    url === createPageUrl(flowItem.page) || url === createPageUrl(appsItem.page)
      ? location.pathname === url || location.pathname.startsWith(url + "/")
      : location.pathname === url;

  // EMBED (?embed=1 from an app's companion overlay — deployed view domain
  // or the in-cortex Apps page): render the routed page CHROMELESS — no nav
  // sidebar, no floating chat, no guided machinery. The host page provides
  // its own frame.
  //
  // The TUTORIAL engine is the exception, and it must be mounted here too.
  // The Guides page is reachable in embed mode (it IS how a deployment shows
  // its guides — the view domain's dial opens /guides?embed=1), and its
  // "Launch tour" dispatches `cortex:start-tutorial`. The listener above runs
  // regardless (hooks precede this return), so the tour state was being set
  // and then never rendered: the click was a silent no-op on every deployment.
  // Mounting TutorialFlow/TutorialChooser here — like ChartViewerManager,
  // which is duplicated for the same reason — is what makes a launched tour
  // actually appear. Tours still only spotlight what exists in this frame;
  // steps anchored to cortex chrome (nav, project chip) fall back to the
  // engine's centered card, which is its documented behaviour for a missing
  // target.
  if (isEmbedMode()) {
    return (
      <SidebarProvider style={{ "--sidebar-width": "0px" }}>
        <div className="h-screen flex w-full overflow-hidden">
          <main className="flex-1 flex flex-col overflow-hidden min-w-0">
            <div className="flex-1 overflow-auto">
              <Outlet />
            </div>
          </main>
        </div>
        {tutorialChooser && (
          <TutorialChooser
            groups={getTourGroupsForProject(projectSlug)}
            onPick={(t) => { setTutorialChooser(false); setTutorial(t); }}
            onClose={() => setTutorialChooser(false)}
          />
        )}
        {tutorial && (
          <TutorialFlow
            tour={tutorial}
            navigate={navigate}
            onClose={() => setTutorial(null)}
          />
        )}
        <ChartViewerManager charts={charts} onClose={(id) => setCharts(prev => prev.filter(c => c.id !== id))} />
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider style={{ "--sidebar-width": "250px" }}>
      <div className="h-screen flex w-full overflow-hidden">
        <Sidebar className="nav-sidebar-bg border-0">
          <div className="flex flex-col gap-[30px] px-3 pt-[30px] pb-5 h-full overflow-hidden">
            <SidebarInner
              isActive={isActive}
              initials={initials}
              fullName={fullName}
              onProfile={() => navigate(createPageUrl("profile"))}
              onLogout={logout}
              theme={theme}
              onToggleTheme={toggleTheme}
              alertsCount={alertsCount}
              boardCount={boardCount}
              activeBranch={branch}
              activeProject={projectSlug}
              sharedAgents={sharedAgents}
            />
          </div>
        </Sidebar>

        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <MobileHeader />
          <div className="flex-1 overflow-auto">
            {/* One guard for EVERY project page, rather than patching each
                page's error slot. Three states, in order:
                  undecided — the project list is still in flight and this route
                    is not in the share-only allowed set, so we cannot yet tell
                    whom we are rendering for. Hold. Mounting the page here is
                    what used to fire its fetches before the redirect landed.
                  blocked   — a share-only user on a route that isn't theirs
                    (typed URL, bookmark, the last-visited route their login
                    replayed). The effect above REDIRECTS to their apps list;
                    rendering nothing meanwhile keeps the Outlet — and its
                    project-scoped fetches — from ever mounting.
                  otherwise — the real page.
                NotSharedWithYou is kept for the one case a redirect cannot
                serve: nowhere to send them (no project slug in the URL).
                Presentational — the server is still the enforcement point. */}
            {undecided
              ? null
              : blockedForShareOnly
                ? (appsUrl ? null : <NotSharedWithYou project={projectSlug} />)
                : <Outlet />}
          </div>
        </main>
      </div>
      {/* Single orchestrator chat — routes to governance/engine/analyst
          sub-agents. The engine harness stays mounted (hidden) so the
          "Add strategy" wizard can hand off to it directly. */}
      <AssistantChat harness="orchestrator" />
      <AssistantChat harness="engine" />
      <EmbedPageModal projectSlug={projectSlug} branch={branch} />
      {guidedActive && (
        <GuidedDemo
          navigate={navigate}
          onClose={() => setGuidedActive(false)}
        />
      )}
      {guidedEntity && getGuide(guidedEntity.key) && (
        <GuidedFlow
          guide={getGuide(guidedEntity.key)}
          ctx={{ kind: guidedEntity.kind }}
          navigate={navigate}
          onClose={() => setGuidedEntity(null)}
        />
      )}
      {tutorialChooser && (
        <TutorialChooser
          groups={getTourGroupsForProject(projectSlug)}
          onPick={(t) => { setTutorialChooser(false); setTutorial(t); }}
          onClose={() => setTutorialChooser(false)}
        />
      )}
      {tutorial && (
        <TutorialFlow
          tour={tutorial}
          navigate={navigate}
          onClose={() => setTutorial(null)}
        />
      )}
      <ChartViewerManager charts={charts} onClose={(id) => setCharts(prev => prev.filter(c => c.id !== id))} />
    </SidebarProvider>
  );
}
